import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, errMsg } from "@finance/lib/api";
import { useAuth } from "@finance/context/AuthContext";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { DateField } from "@finance/components/DateField";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

// Drop-in Reverse / Request-Reverse action for any source document's detail view.
// Loads that document's posted GL entries and offers reversal per the maker-checker rules.
// `inline` renders a single compact control (for list rows); default renders a labelled block.
export function ReverseActions({ sourceId, title = "Reverse accounting entry", inline = false, onDone }) {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const canInitiate = isOwner || ["FM", "AR", "AP"].includes(user?.role_code);
  const [rows, setRows] = useState([]);
  const [revFor, setRevFor] = useState(null);
  const [form, setForm] = useState({ date: today(), reason: "" });

  const load = useCallback(() => {
    if (!sourceId) return;
    api.get("/gl/entries-for-source", { params: { source_id: sourceId } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [sourceId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.reason.trim()) { toast.error("Enter a reason for the reversal"); return; }
    try {
      const { data } = await api.post(`/gl/entries/${revFor.id}/reverse`, { date: form.date, reason: form.reason.trim() });
      toast.success(data.posted ? `Reversed — posted mirror entry #${data.reversal_ref}` : "Reversal request submitted — awaiting approval");
      setRevFor(null); setForm({ date: today(), reason: "" }); load(); onDone?.();
    } catch (e) {
      const msg = errMsg(e);
      if (String(msg).includes("EFD_CREDIT_NOTE_ONLY")) {
        toast.error(String(msg).replace("EFD_CREDIT_NOTE_ONLY: ", ""), { duration: 10000, action: { label: "Raise Credit Note", onClick: () => { setRevFor(null); document.querySelector('[data-testid="credit-debit-notes"]')?.scrollIntoView({ behavior: "smooth" }); } } });
      } else toast.error(msg);
    }
  };

  if (!rows.length || !canInitiate) return null;

  const dialog = (
    <Dialog open={!!revFor} onOpenChange={(v) => { if (!v) { setRevFor(null); setForm({ date: today(), reason: "" }); } }}>
      <DialogContent className="max-w-md" data-testid="src-reverse-dialog">
        <DialogHeader><DialogTitle className="font-display">{isOwner ? "Reverse Entry" : "Request Reversal"}</DialogTitle>
          <DialogDescription>{isOwner ? "Posts a mirror journal (Dr/Cr flipped), netting this entry to zero. The original is preserved for audit." : "Creates a pending request — nothing posts until the authoriser approves it."}</DialogDescription>
        </DialogHeader>
        {revFor && (
          <div className="space-y-3">
            <div className="rounded-sm bg-gray-50 p-3 text-sm"><div className="font-medium text-gray-800">{revFor.memo || revFor.source_type}</div><div className="text-xs text-gray-500">TSh {fmtNum(revFor.amount)}</div></div>
            <div><Label className="lx-label">Reversal date</Label><DateField className="mt-1" data-testid="src-reverse-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label className="lx-label">Reason <span className="text-red-500">*</span></Label><Input className="mt-1" data-testid="src-reverse-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why is this being reversed?" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setRevFor(null)}>Cancel</Button>
          <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="src-reverse-submit" onClick={submit} disabled={!form.reason.trim()}>{isOwner ? "Post Reversal" : "Submit Request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const statusChip = (e) => e.reversed_by ? <span className="rounded-sm bg-red-50 px-1.5 py-0.5 text-red-600">Reversed #{e.reversed_by_ref}</span>
    : e.reversal_pending ? <span className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-amber-700">Reversal pending</span>
    : e.is_reversal ? <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-gray-500">Reversal</span>
    : e.reversible ? <button className="font-medium text-amber-600 hover:underline" data-testid={`src-reverse-${e.id}`} onClick={() => { setRevFor(e); setForm({ date: today(), reason: "" }); }}>{isOwner ? "Reverse" : "Request Reverse"}</button>
    : <span className="text-gray-300">—</span>;

  if (inline) {
    const primary = rows.find((e) => e.reversible) || rows.find((e) => e.reversed_by || e.reversal_pending) || rows[0];
    return (<span className="text-xs" data-testid="reverse-actions-inline">{statusChip(primary)}{dialog}</span>);
  }

  return (
    <div data-testid="reverse-actions">
      <div className="lx-label mb-1 text-gray-400">{title}</div>
      <div className="space-y-1">
        {rows.map((e) => (
          <div key={e.id} className="flex items-center justify-between rounded-sm border border-gray-100 px-2 py-1.5 text-xs">
            <div className="min-w-0 truncate"><span className="text-gray-600">{e.memo || e.source_type}</span> <span className="lx-num text-gray-400">· TSh {fmtNum(e.amount)}</span></div>
            <div className="shrink-0 pl-2">{statusChip(e)}</div>
          </div>
        ))}
      </div>
      {dialog}
    </div>
  );
}
